/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import { CrossTabCoordinator, DEFAULT_CROSS_TAB_CONFIG } from "../app/session/CrossTabCoordinator";
import { forceRefreshSessionAsync } from "../app/sessionSlice";
import store from "../app/store";
import getConfig from "../config";
import { fetchAvaAppAccessToken } from "./avaAppToken";
import { cleanupSessionRefreshTimeout } from "./sessionTimeoutCleanup";
import { clearLocalStorage, isOneWebDomainRuntime, retainCriticalData } from "./Utils";

// Config is retrieved lazily inside functions to support runtime configuration
const getEcmApiHost = () => getConfig().hostnames.ecmApiHost;

// Safety margin before the JWT's exp claim. If the access token in Redux is
// within this window of expiring we proactively refresh before handing it to
// callers (AuiUploader, AuiHeader, AuiSidenav, etc.) so a long-running call
// like FVS /init does not fail mid-flight with a 401.
const TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

/**
 * Returns true when the access token in the given session slice is at or past
 * its expiry (with TOKEN_EXPIRY_BUFFER_MS safety margin).
 *
 * Sessions that pre-date the accessTokenExpiresAt field (legacy shape) are
 * treated as not expired so existing flows continue to work unchanged.
 *
 * @param {Object} session - The session slice from the Redux store
 * @returns {boolean}
 */
const isAccessTokenExpired = session => {
    if (!session?.accessTokenExpiresAt) {
        return false;
    }
    const expiresAtMs = session.accessTokenExpiresAt * 1000;
    return Date.now() >= expiresAtMs - TOKEN_EXPIRY_BUFFER_MS;
};

// Module-level in-flight refresh Promise. When several consumers ask for a
// token in the same tick and Redux holds an expired token, we want exactly
// one /api/v3/session call to go out and all callers to await the same
// Promise. forceRefreshSessionAsync has an isLoading short-circuit that
// returns immediately without waiting, so we need our own dedupe to make
// every caller observe the freshly-refreshed Redux state before resolving.
let inFlightRefreshPromise = null;

const refreshSessionOnce = () => {
    if (inFlightRefreshPromise) {
        return inFlightRefreshPromise;
    }
    // User-initiated refresh (so isLoading flips and other callers can short-
    // circuit), bypassing the min-interval so we are never blocked by a
    // recent prior refresh attempt.
    inFlightRefreshPromise = Promise.resolve(
        store.dispatch(forceRefreshSessionAsync(false, true))
    ).finally(() => {
        inFlightRefreshPromise = null;
    });
    return inFlightRefreshPromise;
};

/**
 * @param page {string | undefined}
 */
export const getPageTitle = page => (dispatch, getState) => {
    const { session } = getState();
    return ["Avalara", session?.activeCompany?.name, page].filter(Boolean).join(" | ");
};

export const getSessionAccessToken = () => {
    return new Promise((resolve, reject) => {
        const MAX_RETRY_ATTEMPTS = 6;
        const INITIAL_DELAY_MS = 100;
        const MAX_DELAY_MS = 2000;
        let attemptCount = 0;
        let isSettled = false;

        const settleWithRefreshedToken = onUnavailable => {
            refreshSessionOnce()
                .then(() => {
                    const { session: refreshed } = store.getState();
                    if (
                        refreshed?.accessToken &&
                        refreshed?.isAuthenticated &&
                        !isAccessTokenExpired(refreshed)
                    ) {
                        resolve({ accessToken: refreshed.accessToken });
                    } else {
                        onUnavailable(refreshed);
                    }
                })
                .catch(error => {
                    reject(error instanceof Error ? error : new Error(String(error)));
                });
        };

        const checkTokenAvailability = () => {
            if (isSettled) {
                return;
            }

            attemptCount++;
            const { session } = store.getState();

            // Fast-fail on explicit authentication failure
            if (session?.isAuthenticated === false && session?.isLoading === false) {
                isSettled = true;
                reject(new Error("Authentication failed - user not authenticated"));
                return;
            }

            const isSessionReady =
                session?.accessToken &&
                session?.activeCompany?.id &&
                session?.isLoading === false &&
                session?.isAuthenticated === true;

            // AvaApp: always fetch a fresh shell token per call. Redux expiry
            // fields reflect ECM session timing, not the short-lived AvaApp JWT.
            if (isSessionReady && isOneWebDomainRuntime()) {
                isSettled = true;
                fetchAvaAppAccessToken()
                    .then(token => resolve({ accessToken: token }))
                    .catch(error => {
                        reject(error instanceof Error ? error : new Error(String(error)));
                    });
                return;
            }

            // Happy path: session is otherwise valid AND the token still has
            // life. Hand it back without any extra work or network call.
            if (isSessionReady && !isAccessTokenExpired(session)) {
                isSettled = true;
                resolve({ accessToken: session.accessToken });
                return;
            }

            // Session is alive but the access token has expired (or is within
            // the safety buffer). Refresh once and resolve with the fresh
            // token so downstream consumers never see a stale JWT.
            if (isSessionReady && isAccessTokenExpired(session)) {
                isSettled = true;
                settleWithRefreshedToken(() => {
                    reject(new Error("Token refresh completed but no valid token is available"));
                });
                return;
            }

            if (attemptCount >= MAX_RETRY_ATTEMPTS) {
                isSettled = true;
                if (isOneWebDomainRuntime()) {
                    // In the AvaApp context we cannot obtain a token without a ready
                    // Redux session, so reject rather than loop forever.
                    reject(
                        new Error(
                            `Session state not ready after ${MAX_RETRY_ATTEMPTS} attempts in AvaApp context. Current state: ${JSON.stringify(
                                {
                                    hasAccessToken: !!session?.accessToken,
                                    hasCompanyId: !!session?.activeCompany?.id,
                                    isLoading: session?.isLoading,
                                    isAuthenticated: session?.isAuthenticated,
                                }
                            )}`
                        )
                    );
                } else {
                    settleWithRefreshedToken(refreshedSession => {
                        const errorMessage = `Session state not ready after ${MAX_RETRY_ATTEMPTS} attempts. Current state: ${JSON.stringify(
                            {
                                hasAccessToken: !!refreshedSession?.accessToken,
                                hasCompanyId: !!refreshedSession?.activeCompany?.id,
                                isLoading: refreshedSession?.isLoading,
                                isAuthenticated: refreshedSession?.isAuthenticated,
                                tokenExpired: isAccessTokenExpired(refreshedSession),
                            }
                        )}`;
                        reject(new Error(errorMessage));
                    });
                }
                return;
            }

            const delay = Math.min(INITIAL_DELAY_MS * Math.pow(2, attemptCount - 1), MAX_DELAY_MS);

            setTimeout(checkTokenAvailability, delay);
        };

        checkTokenAvailability();
    });
};

let crossTabCoordinator;
try {
    crossTabCoordinator = CrossTabCoordinator.getInstance(DEFAULT_CROSS_TAB_CONFIG);
} catch {
    // If coordinator initialization fails, set to null
    // The session refresh functions will handle this gracefully
    // eslint-disable-next-line no-unused-vars
    crossTabCoordinator = null;
}

export const redirectToLoginPage = () => {
    // Clean up session refresh timeout to prevent memory leaks
    cleanupSessionRefreshTimeout();

    // Step 1: Preserve current URL for post-login redirect
    // Critical for User Experience: Ensures user returns to the correct context
    // after successful login. Uses sessionStorage (tab-specific) rather than
    // localStorage (shared across tabs) to preserve each tab's individual context.
    let redirectUrl = window.location.href;
    try {
        // Store the current URL in sessionStorage for this specific tab
        sessionStorage.setItem("redirect_url", redirectUrl);
        // Retrieve it back to ensure storage succeeded (defensive programming)
        redirectUrl = sessionStorage.getItem("redirect_url") || redirectUrl;
    } catch {
        // Handle sessionStorage failures gracefully:
        // - Private browsing mode (sessionStorage may be disabled)
        // - Storage quota exceeded
        // - Security restrictions
        //
        // Fallback: Use current URL directly without storage
        // User will still be redirected correctly, just without persistence
    }

    // Step 2: Prepare for clean authentication session
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);

    // Retain critical application data that should survive logout
    retainCriticalData();

    // Clear localStorage to remove stale session data
    // This prevents conflicts between old and new authentication sessions
    clearLocalStorage();

    // Step 3: Perform the actual redirect to login
    // The redirectUrl parameter ensures the user returns to the correct context
    // after successful authentication. The login service will use this URL
    // to redirect the user back to their original location.
    window.location.href = `//${getEcmApiHost()}/api/v3/auth/login?redirectUrl=${encodedRedirectUrl}`;
};
